// MF-554 — Chart Library panel of the Board Builder: quick search (name, descriptions, labels, data
// source), grouped. Click "+" (or drag it onto the grid) to add a tile; the preview button opens the
// item's detail with sample data. "Browse all" opens the full catalog (filters by group / label /
// kind) for when the Library is too big to scroll. Items the school can't feed (D11) are greyed out.
import { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SUMMARY_PALETTE } from '../boardTheme'
import ItemChips from '../Library/ItemChips'
import LibraryBrowser from '../Library/LibraryBrowser'
import LibraryItemDialog from '../Library/LibraryItemDialog'
import { matches } from '../Library/libraryUtils'

const LibraryPanel = ({ items, onAdd, onDragItem, onHide }) => {
  const [q, setQ] = useState('')
  const [detail, setDetail] = useState(null) // item shown in the detail dialog
  const [browse, setBrowse] = useState(false)
  const groups = useMemo(() => {
    const byGroup = new Map()
    for (const i of (items || []).filter((x) => matches(x, q)))
      byGroup.set(i.group, [...(byGroup.get(i.group) || []), i])
    return [...byGroup.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [items, q])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: SUMMARY_PALETTE.ink, flex: 1 }}>
          Chart Library
        </Typography>
        <Button size='small' onClick={() => setBrowse(true)} sx={{ textTransform: 'none' }}>
          Browse all
        </Button>
        {onHide ? (
          <Tooltip title='Hide the library'>
            <IconButton size='small' onClick={onHide} aria-label='Hide the library'>
              <FontAwesomeIcon icon={['fal', 'chevron-left']} style={{ fontSize: 12 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
      <TextField
        size='small'
        placeholder='Search name, label, source…'
        value={q}
        onChange={(e) => setQ(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <FontAwesomeIcon icon={['fal', 'search']} />
            </InputAdornment>
          )
        }}
      />
      <Box sx={{ overflowY: 'auto', mt: 1.5, pr: 0.5, flex: 1, minHeight: 0 }}>
        {items === null ? (
          [0, 1, 2, 3].map((i) => <Skeleton key={i} height={64} />)
        ) : groups.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted }}>Nothing matches.</Typography>
        ) : (
          groups.map(([group, list]) => (
            <Box key={group} sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '.06em',
                  textTransform: 'uppercase',
                  color: SUMMARY_PALETTE.faint,
                  mb: 0.5
                }}
              >
                {group}
              </Typography>
              {list.map((item) => {
                const ok = item.compatible
                return (
                  <Box
                    key={item.code}
                    draggable={ok}
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', item.code)
                      if (onDragItem) onDragItem(item)
                    }}
                    onDragEnd={() => onDragItem && onDragItem(null)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      p: 1,
                      mb: 0.75,
                      borderRadius: '10px',
                      border: `1px solid ${SUMMARY_PALETTE.cardBorder}`,
                      bgcolor: '#fff',
                      opacity: ok ? 1 : 0.55,
                      cursor: ok ? 'grab' : 'default'
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: SUMMARY_PALETTE.ink }}>
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: SUMMARY_PALETTE.muted,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mt: 0.25
                        }}
                      >
                        {item.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                        <ItemChips item={item} />
                      </Box>
                    </Box>
                    <Tooltip title='Preview and details'>
                      <IconButton
                        size='small'
                        onClick={() => setDetail(item)}
                        aria-label={`Preview ${item.title}`}
                      >
                        <FontAwesomeIcon icon='expand-alt' style={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={ok ? 'Add to board' : item.reason || 'No data for this school'}>
                      <span>
                        <IconButton
                          size='small'
                          disabled={!ok}
                          onClick={() => onAdd(item)}
                          aria-label={`Add ${item.title}`}
                        >
                          <FontAwesomeIcon icon={['fal', 'plus']} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )
              })}
            </Box>
          ))
        )}
      </Box>

      <LibraryItemDialog
        item={detail}
        onClose={() => setDetail(null)}
        onAdd={(item) => {
          setDetail(null)
          onAdd(item)
        }}
      />
      <Dialog open={browse} onClose={() => setBrowse(false)} maxWidth='xl' fullWidth>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 3, pt: 2 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: SUMMARY_PALETTE.ink, flex: 1 }}>
            Chart Library
          </Typography>
          <IconButton onClick={() => setBrowse(false)} aria-label='Close'>
            <FontAwesomeIcon icon={['fal', 'times']} />
          </IconButton>
        </Box>
        <DialogContent sx={{ minHeight: '70vh' }}>
          <LibraryBrowser
            items={items}
            onAdd={(item) => {
              setBrowse(false)
              onAdd(item)
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default LibraryPanel
